import { Link } from "react-router-dom";
import { useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { FadeUp } from "../components/motion";
import CyclingImage from "../components/CyclingImage";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import { SITE } from "../data/site";
import { useServiceAreas, useSettings } from "../hooks/useCms";

const CITIES = {
  boise: {
    name: "Boise",
    intro: "From the North End to the Bench and the Foothills beyond, Boise is where Idaho living is at its best — and where a thoughtfully built home makes all the difference.",
    body: [
      "On Point Homes builds custom homes throughout Boise and the greater Treasure Valley. Whether you already own a lot in the Foothills, are eyeing an infill property closer to downtown, or are still searching for the right piece of land, we bring the same personal, hands-on approach to every Boise-area build.",
      "As a small, local builder — not a production company — you'll work directly with your builder from the first conversation through the final walkthrough. Straight answers on budget and schedule, daily site walks, and craftsmanship that holds up to a close look.",
    ],
    note: "Serving Boise, the Bench, the Foothills, and surrounding neighborhoods",
    start: 2,
  },
  meridian: {
    name: "Meridian",
    intro: "One of the fastest-growing cities in Idaho — and a place we know well. Our recent work includes completed homes in Meridian's newest communities.",
    body: [
      "On Point Homes builds custom homes across Meridian, from established neighborhoods to new communities like Lavender Heights, where one of our recently completed homes now sits across from Discovery Park.",
      "Meridian offers some of the Treasure Valley's best lots for new construction — generous parcels, family-friendly communities, and quick freeway access. If you're considering building in Meridian, let's walk your lot and talk through what's possible.",
    ],
    note: "Serving Meridian, including new communities along the western corridor",
    start: 8,
  },
  eagle: {
    name: "Eagle",
    intro: "River frontage, mature trees, and some of the Treasure Valley's most beautiful lots — Eagle rewards homes that are designed with intention.",
    body: [
      "On Point Homes builds custom homes in Eagle for clients who care about the details. Larger lots and established neighborhoods here deserve architecture that fits the setting — thoughtful design, honest materials, and craftsmanship in every corner.",
      "From your first ideas to the final walkthrough, you'll work directly with your builder. No handoffs, no call centers — just a personal building experience and a home that belongs exactly where it sits.",
    ],
    note: "Serving Eagle and the surrounding river communities",
    start: 16,
  },
  star: {
    name: "Star",
    intro: "Small-town feel with room to breathe — Star is one of the Treasure Valley's best-kept secrets for building a custom home.",
    body: [
      "On Point Homes builds custom homes in Star and the surrounding countryside, where larger lots and open views give a well-designed home room to shine.",
      "If you're dreaming of a home with space — for a shop, an RV bay, animals, or simply quiet evenings — Star is a wonderful place to build, and we'd be glad to walk your property and plan it together.",
    ],
    note: "Serving Star and the northern Treasure Valley corridor",
    start: 24,
  },
  middleton: {
    name: "Middleton",
    intro: "River living on the Boise River's quieter edge — Middleton is home to River Walk Ranch, the waterfront community where we're currently building.",
    body: [
      "On Point Homes is an active builder in Middleton's River Walk Ranch — a master-planned community built around four ponds with private beaches, walking paths, and direct Boise River Greenbelt access. Two of our current available homes are there now.",
      "Middleton offers larger lots, a slower pace, and quick access to both Caldwell and the freeway. If waterfront mornings and room for an RV bay sound right, let's talk about building here.",
    ],
    note: "Serving Middleton, including River Walk Ranch and the Boise River corridor",
    start: 1,
    communities: [
      { name: "River Walk Ranch — Official Community Site", url: "https://riverwalkmiddleton.com/" },
      { name: "River Walk Ranch on Instagram", url: "https://www.instagram.com/riverwalkmiddleton" },
    ],
  },
  nampa: {
    name: "Nampa",
    intro: "The Treasure Valley's second-largest city — and home to Osprey Estates, where several of our completed homes now stand.",
    body: [
      "On Point Homes has built multiple homes in Nampa's Osprey Estates community, a neighborhood known for its walking paths, wide-open common spaces, and panoramic views — including recent homes on Chicken Hawk Loop with 40-foot RV bays and smart-home features throughout.",
      "Nampa gives buyers real choice: established neighborhoods, new communities, and acreage properties on the edge of town. Wherever your lot is, we bring the same personal, hands-on build.",
    ],
    note: "Serving Nampa, including Osprey Estates and south Nampa communities",
    start: 6,
    communities: [
      { name: "Osprey Estates — Official Community Site", url: "https://ospreyestates-idaho.com/" },
      { name: "Osprey Estates on Instagram", url: "https://www.instagram.com/ospreyestates.idaho" },
    ],
  },
  caldwell: {
    name: "Caldwell",
    intro: "A city on the rise — Caldwell pairs genuine small-town character with some of the best value land in the Treasure Valley.",
    body: [
      "On Point Homes builds custom homes in Caldwell, where larger parcels and a revitalized downtown are drawing families who want space without leaving the valley.",
      "From acreage builds to neighborhood infill, we'll walk your property, talk honestly about budget and timeline, and build a home designed around how you live.",
    ],
    note: "Serving Caldwell and the western Treasure Valley",
    start: 12,
  },
  kuna: {
    name: "Kuna",
    intro: "South of the hubbub and growing fast — Kuna offers open skies, new communities, and lots that give a custom home room to breathe.",
    body: [
      "On Point Homes builds custom homes in Kuna and the surrounding south-valley countryside, where new communities and rural lots sit minutes from Meridian and the freeway.",
      "If you want a thoughtfully designed home with space for a shop, a big garage, or simply a little quiet, Kuna is a smart place to build — and we'd love to show you what's possible.",
    ],
    note: "Serving Kuna and the southern Treasure Valley",
    start: 19,
  },
};

const CityPage = () => {
  const { slug } = useParams();
  const areas = useServiceAreas();
  const settings = useSettings();
  const local = CITIES[slug];
  const apiArea = areas.find((a) => a.slug === slug);
  const city = apiArea
    ? { name: apiArea.name, intro: apiArea.intro, body: apiArea.body?.length ? apiArea.body : local?.body, note: apiArea.note || local?.note, communities: local?.communities, start: local?.start ?? 8 }
    : local;
  if (!city) return <Navigate to="/" replace />;
  const otherCities = (areas.length ? areas.map((a) => a.name) : Object.values(CITIES).map((c2) => c2.name)).filter((n) => n !== city.name);

  return (
    <main>
      <SEO
        title={`Custom Home Builder in ${city.name}, Idaho | On Point Homes`}
        description={`On Point Homes is a personal, hands-on custom home builder serving ${city.name}, Idaho. Thoughtfully designed new construction and custom homes in the Treasure Valley.`}
      />
      <PageHeader
        testid="city-header"
        eyebrow="Where We Build"
        lines={[{ text: "Building in" }, { text: `${city.name}, Idaho`, italic: true }]}
        description={city.intro}
      />

      <section className="pb-24 md:pb-36">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-6 img-zoom relative aspect-[4/3]">
            <CyclingImage start={city.start} interval={5500} alt={`Custom home built by On Point Homes near ${city.name}, Idaho`} />
          </FadeUp>
          <FadeUp className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center" delay={0.15}>
            {city.body.map((p, i) => (
              <p key={i} className="text-[#5C5A56] leading-relaxed text-base md:text-lg mb-5">{p}</p>
            ))}
            <p className="flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold text-[#1A1A1A] mt-2">
              <MapPin size={15} className="text-[#8C6A4F] shrink-0" /> {city.note}
            </p>
            {city.communities && (
              <div className="mt-6 pt-6 border-t border-[rgba(26,26,26,0.12)]" data-testid="city-communities">
                <p className="eyebrow text-[#8C6A4F] mb-4">Communities We Build In</p>
                <div className="flex flex-col gap-3">
                  {city.communities.map((c) => (
                    <a
                      key={c.url}
                      href={c.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`community-link-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="link-underline w-fit inline-flex items-center gap-2 text-sm font-medium text-[#1A1A1A] hover:text-[#8C6A4F] transition-colors duration-300"
                    >
                      {c.name} <ArrowRight size={13} className="-rotate-45" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="city-cta-btn" className="btn-sweep bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 inline-flex items-center gap-3">
                Start Your Project <ArrowRight size={14} />
              </Link>
              <Link to="/available-homes" data-testid="city-homes-link" className="border border-[#1A1A1A]/30 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-500">
                View Available Homes
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-20 bg-[#EFECE7]">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight max-w-xl">
            Also building in {otherCities.join(", ")} and across the Treasure Valley.
          </h2>
          <p className="font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold text-[#5C5A56]">{settings.phone || SITE.phone}</p>
        </div>
      </section>
    </main>
  );
};

export default CityPage;
